import { Store } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { apiErrorMessage } from '../api/client'
import { servicesApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { OFFER_SECTION_BLURB, OFFER_SECTION_LABEL } from '../utils/institutionOffer'
import { ErrorMessage } from '../components/ErrorMessage'
import { ServicesSection } from '../components/ServicesSection'
import type { ProviderServiceResponse } from '../types/api'

export function ServicesPage() {
  const { profile } = useAuth()
  const [services, setServices] = useState<ProviderServiceResponse[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    servicesApi
      .listOwn()
      .then(setServices)
      .catch((err) => setError(apiErrorMessage(err, 'Could not load your services')))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (profile?.persona === 'SUPPLY') load()
  }, [profile, load])

  if (!profile) return <div className="text-ink-500">Loading...</div>

  // Only the supply side has services to keep. A learner arriving here by URL belongs on their
  // own profile, not on an empty page telling them so.
  if (profile.persona !== 'SUPPLY') return <Navigate to="/profile" replace />

  return (
    <>
      <div className="mb-6">
        {/* Named for what this entity actually publishes. A school reading "My services" has to
            translate it every time, and "what you offer" is not how a school describes admitting
            a class. */}
        <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
          <Store size={18} className="text-sun-500" />
          {OFFER_SECTION_LABEL[profile.entityKind] ?? 'My services'}
        </h1>
        <p className="text-sm text-ink-500">
          {OFFER_SECTION_BLURB[profile.entityKind] ??
            'What you offer, and how nearby learners find you.'}
        </p>
      </div>

      <ErrorMessage message={error} />

      {loading ? (
        <div className="text-ink-500">Loading your services...</div>
      ) : (
        <ServicesSection services={services} onChanged={load} />
      )}
    </>
  )
}
